import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconArrowLeft } from "@velaro/velaro-component-lib/";
import IconByName from "@/components/ds/IconByName";
import { PrimaryButton } from "@/components/ds";
import IntegrationActivityLog from "../IntegrationActivityLog";

type Tab = "overview" | "activity";

export default function ZendeskOverview() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("overview");

  return (
    <div>
      <div className="font-semibold group flex items-center gap-2 justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-14 h-14 border-zinc-300 relative mr-4 rounded border p-2 content-center flex items-center justify-center">
            <img src="/img/zendesk.svg" alt="Zendesk" className="w-10 h-10" />
          </div>
          <div>
            <div className="font-semibold text-slate-gray-800 text-lg">Zendesk</div>
            <div className="font-normal text-slate-gray-400 text-sm">
              Show live Velaro chat history in the Zendesk ticket sidebar — no separate CRM lookup needed.
            </div>
          </div>
        </div>
        <PrimaryButton
          label="Generate an API key"
          onClick={() => navigate("/settings/api-keys")}
          prefixIcon={null}
          className="font-medium"
        />
      </div>

      <div className="flex border-b mb-4">
        <button
          type="button"
          onClick={() => setTab("overview")}
          className={`py-2 mr-4 cursor-pointer text-sm px-3 border-b-2 ${tab === "overview" ? "text-zinc-800 border-b-zinc-600 font-medium" : "text-zinc-500 border-b-transparent"}`}
        >
          Overview
        </button>
        <button
          type="button"
          onClick={() => setTab("activity")}
          className={`py-2 mr-4 cursor-pointer text-sm px-3 border-b-2 ${tab === "activity" ? "text-zinc-800 border-b-zinc-600 font-medium" : "text-zinc-500 border-b-transparent"}`}
        >
          Activity Log
        </button>
      </div>

      {tab === "overview" && (
        <>
          <div className="text-sm text-zinc-800 space-y-3">
            <p>
              The Velaro Chat History app is a Zendesk Apps Framework (ZAF) ticket-sidebar app. Once installed,
              every ticket shows a live panel for the ticket requester: total chats, average CSAT, AI-resolved
              rate, and a rich card per recent conversation — channel, duration, queue wait, status, AI-detected
              sentiment and topic (if enabled on your plan), an AI-generated summary, and a "Converted" badge.
            </p>
            <p>
              Unlike our HubSpot and Dynamics 365 cards, Zendesk does not need an OAuth connection — the app
              authenticates with a Velaro service API key instead, which Zendesk stores encrypted and never
              exposes to the sidebar's JavaScript.
            </p>
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800 space-y-2">
            <p className="font-medium">Install steps (full detail: <code className="bg-blue-100 px-1 rounded">tools/zendesk-app/README.md</code>):</p>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>
                Click <strong>Generate an API key</strong> above (or go to Settings → API Keys) and create one
                labeled "Zendesk sidebar app"
              </li>
              <li>Download <code className="bg-blue-100 px-1 rounded">tools/zendesk-app</code> from the Velaro repo, or ask support for the packaged app zip</li>
              <li>In Zendesk, go to <strong>Admin Center → Apps and integrations → Zendesk Support apps → Upload private app</strong></li>
              <li>
                Upload the zip, then set both install parameters: <strong>adminUrl</strong> (leave as{" "}
                <code className="bg-blue-100 px-1 rounded">https://admin.velaro.com</code> for production, or change to{" "}
                <code className="bg-blue-100 px-1 rounded">https://admin-staging.velaro.com</code> for staging) and{" "}
                <strong>apiKey</strong> — your API key from step 1 (this field is a secure, encrypted setting)
              </li>
              <li>Open any ticket — the Velaro Chat History panel appears in the sidebar automatically</li>
            </ol>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                title: "Live Chat History",
                desc: "See every past Velaro conversation for the ticket requester — status, channel, duration, and CSAT — without leaving Zendesk.",
                icon: "Search"
              },
              {
                title: "AI Insights",
                desc: "AI Resolved badges, detected sentiment and topic, and an AI-generated conversation summary — gated to your plan's AI features.",
                icon: "Bot"
              },
              {
                title: "Conversion Attribution",
                desc: "A \"Converted\" badge shows when a chat led to a tracked conversion, so agents see the full customer picture instantly.",
                icon: "Plus"
              }
            ].map((f) => (
              <div key={f.title} className="rounded-lg border border-zinc-200 p-4">
                <div className="text-zinc-600 mb-2"><IconByName name={f.icon} className="w-6 h-6" /></div>
                <div className="font-medium text-zinc-800 text-sm mb-1">{f.title}</div>
                <div className="text-xs text-zinc-500">{f.desc}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "activity" && <IntegrationActivityLog integrationSlug="Zendesk" hideSkillsTab />}
    </div>
  );
}

export function ZendeskBackToIntegrationLanding() {
  const navigate = useNavigate();
  return (
    <div className="flex cursor-pointer items-center gap-2 text-zinc-600" onClick={() => navigate("/integrations")}>
      <IconArrowLeft className="h-6 w-6" />
      Back
    </div>
  );
}
